// Modo de operação do cardápio, decidido pela URL:
//   /:namespace                                 -> delivery
//   /:namespace/(mesa|comanda)/:mesaId          -> inloco
//   /:namespace/autoatendimento/<tableId>       -> um dos submodos abaixo
//
// Submodos de autoatendimento, pelo tableId:
//   pos       cardápio exibido no próprio POS
//   qrcode    cliente pede no celular e paga no POS digitando o nº do pedido
//   totem-*   tela touch vinculada a um POS
//   tablet-*  tablet de mesa, sem pagamento (tablet-<local>-<numero>-<id>)
//   mesa-*    link antigo do tablet de mesa; vira tablet-mesa-*
//
// Um tableId de autoatendimento fora desses formatos cai em "inloco".
export const MODE = Object.freeze({
  DELIVERY: "delivery",
  IN_LOCO: "inloco",
  POS: "pos",
  QRCODE: "qrcode",
  TOTEM: "totem",
  TABLE: "table",
});

const SELFCHECKOUT_MODES = [MODE.POS, MODE.QRCODE, MODE.TOTEM, MODE.TABLE];

export function isSelfcheckoutMode(mode) {
  return SELFCHECKOUT_MODES.includes(mode);
}

function selfcheckoutModeOf(tableId) {
  if (tableId === "pos") return MODE.POS;
  if (tableId === "qrcode") return MODE.QRCODE;
  if (tableId.startsWith("totem-")) return MODE.TOTEM;
  if (tableId.startsWith("tablet-") || tableId.startsWith("mesa-")) {
    return MODE.TABLE;
  }
  return MODE.IN_LOCO;
}

/**
 * Traduz os parâmetros da URL no modo e nas flags que o resto do app lê.
 * As flags booleanas continuam existindo porque as regras de negócio
 * (checkout, status, pagamento) dependem delas; o `mode` é o que decide qual
 * layout cada tela recebe.
 */
export function parseSelfcheckoutMode({ orderLocation, isInLoco, tableId }) {
  const rawTableId = tableId || "";
  let mode = MODE.DELIVERY;
  if (isInLoco) {
    mode =
      orderLocation === "autoatendimento"
        ? selfcheckoutModeOf(rawTableId)
        : MODE.IN_LOCO;
  }

  let normalizedTableId = isInLoco && rawTableId ? decodeURI(rawTableId) : "";
  // compatibilidade com os links antigos do tablet (mesa-1-1329)
  if (mode === MODE.TABLE && rawTableId.startsWith("mesa-")) {
    normalizedTableId = "tablet-" + rawTableId;
  }

  const isTable = mode === MODE.TABLE;

  return {
    mode,
    tableId: normalizedTableId,
    isSelfcheckout: isSelfcheckoutMode(mode),
    isSelfcheckoutPOS: mode === MODE.POS,
    isSelfcheckoutQRCode: mode === MODE.QRCODE,
    // o tablet de mesa usa o layout do totem, por isso liga as duas flags
    isSelfcheckoutTotem: mode === MODE.TOTEM || isTable,
    isSelfOrderTable: isTable,
    isSelfOrderScanQRCode:
      isTable &&
      parseTableLocation(normalizedTableId).config === "comandaqrcode",
  };
}

/**
 * Lê o local do tablet de mesa: tablet-<local>-<numero>-<config>.
 *   tablet-mesa-1-1329             -> MESA 1
 *   tablet-balcao-23-comandaqrcode -> BALCAO 23, lê a comanda por QR Code
 */
export function parseTableLocation(tableId) {
  const [, locationString = "", locationNumber = "", config] = (
    tableId || ""
  ).split("-");
  const title = locationString.trim().toUpperCase();

  return {
    title,
    text: (title + " " + locationNumber).trim(),
    config,
  };
}

// O que cada modo recebe de layout. É o único lugar que decide entre a versão
// do celular e a do totem de cada tela; as telas leem daqui em vez de combinar
// flags. Regras de negócio (pagamento, status, envio) seguem nas flags.
//
//   mobileUi  body.ui-mobile, zoom liberado e manifest da loja
//   totemUi   body.ui-totem: tela grande (totem, tablet de mesa)
//   maxWidth  limita a largura a 760px (celular aberto no computador)
//   home      "delivery" | "pos" | "totem"
//   product   tela de produto: "mobile" | "totem"
//   cart      resumo flutuante da sacola na home: "mobile" | "totem"
//   checkout  sacola e checkout: "mobile" (cards e formulário na página) |
//             "totem" (itens em cartões, rodapé com Cancelar/Finalizar e o
//             passo a passo em tela cheia)
//   restWall  tela de descanso e limpeza da sacola após inatividade
//   upsell    "Peça também" na sacola (CartUpsell / CartUpsellTotem)
//   sharedDevice  aparelho da loja, usado por vários clientes: nada do que um
//                 cliente respondeu (maioridade, por ex.) vale para o próximo
const MOBILE_LAYOUT = Object.freeze({
  mobileUi: true,
  totemUi: false,
  maxWidth: true,
  home: "delivery",
  product: "mobile",
  cart: "mobile",
  checkout: "mobile",
  restWall: false,
  upsell: true,
  sharedDevice: false,
});

const TOTEM_LAYOUT = Object.freeze({
  mobileUi: false,
  totemUi: true,
  maxWidth: false,
  home: "totem",
  product: "totem",
  cart: "totem",
  checkout: "totem",
  restWall: true,
  upsell: true,
  sharedDevice: true,
});

export const LAYOUT_BY_MODE = Object.freeze({
  [MODE.DELIVERY]: MOBILE_LAYOUT,
  [MODE.IN_LOCO]: MOBILE_LAYOUT,
  // O QR Code é o celular do próprio cliente: layout do celular inteiro. Só o
  // conteúdo do checkout muda (SelfCheckoutQRCodeForm).
  [MODE.QRCODE]: MOBILE_LAYOUT,
  // O POS é um celular com WebView antigo: home e barra da sacola do celular,
  // tela de produto e checkout do totem (em tamanho de celular), sem o "Peça
  // também": a tela pequena já fica cheia com a sacola.
  [MODE.POS]: Object.freeze({
    mobileUi: false,
    totemUi: false,
    maxWidth: false,
    home: "pos",
    product: "totem",
    cart: "mobile",
    checkout: "totem",
    restWall: true,
    upsell: false,
    sharedDevice: true,
  }),
  [MODE.TOTEM]: TOTEM_LAYOUT,
  [MODE.TABLE]: TOTEM_LAYOUT,
});

export function layoutOf(shop) {
  return LAYOUT_BY_MODE[shop.mode] || MOBILE_LAYOUT;
}
